import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { type StatesAdminV2Query, statesAdminV2Service } from '../services/states-admin-v2.service'

type UseStatesAdminV2QueryArgs = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize: number
}

/**
 * Listado de estados v2 con paginación por cursor y navegación prev/next por PILA
 * (ADR 0019). Espeja los hooks de la ola: la pila guarda el cursor `after` de cada
 * página; goNext apila el `nextCursor`, goPrev desapila; cambiar
 * filtros/orden/búsqueda/tamaño reinicia la pila (ajuste de estado en render).
 */
export function useStatesAdminV2Query({
  filters,
  sort,
  search,
  pageSize
}: UseStatesAdminV2QueryArgs) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''

  const resetKey = JSON.stringify({ filters, sort, search, pageSize })
  const [cursorState, setCursorState] = useState<{
    key: string
    stack: (string | undefined)[]
  }>({ key: resetKey, stack: [undefined] })
  if (cursorState.key !== resetKey) {
    setCursorState({ key: resetKey, stack: [undefined] })
  }
  const after = cursorState.stack[cursorState.stack.length - 1]

  const listQuery: StatesAdminV2Query = { filters, sort, search, pageSize, after }
  const query = useQuery({
    queryKey: queryKeys.states.adminListV2({ filters, sort, search, pageSize, after }),
    queryFn: async () => await statesAdminV2Service.list(accessToken, listQuery),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Un 403/500 es determinista: reintentarlo sólo retrasa el aviso al usuario.
    retry: false
  })

  const nextCursor = query.data?.pagination.nextCursor
  const goNext = useCallback(() => {
    if (!nextCursor) return
    setCursorState((state) => ({ ...state, stack: [...state.stack, nextCursor] }))
  }, [nextCursor])

  const goPrev = useCallback(() => {
    setCursorState((state) =>
      state.stack.length > 1 ? { ...state, stack: state.stack.slice(0, -1) } : state
    )
  }, [])

  return {
    query,
    rows: query.data?.data ?? [],
    page: cursorState.stack.length,
    canPrev: cursorState.stack.length > 1,
    canNext: query.data?.pagination.hasMore ?? false,
    goNext,
    goPrev
  }
}

/** Totales y facetas active/type del listado v2 (mismos filtros, sin paginar). */
export function useStatesAdminV2Summary(args: {
  filters?: Record<string, unknown>
  search?: string
}) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.states.summaryV2(args),
    queryFn: async () => await statesAdminV2Service.summary(accessToken, args),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Un 403/500 es determinista: reintentarlo sólo retrasa el aviso al usuario.
    retry: false
  })
}

/** Detalle de UN estado por `sta_`. Alimenta el editor en modo edición. */
export function useStateV2DetailQuery(id: string | null) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.states.detailV2(id),
    queryFn: async () => await statesAdminV2Service.detail(accessToken, id as string),
    enabled: Boolean(accessToken) && Boolean(id),
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Requisitos documentales del portal de UN estado. Query aparte del detalle
 * porque son un subrecurso con su propia escritura: se refrescan cuando cambian
 * ellos, no cuando cambia el nombre del estado.
 */
export function useStateV2AttachmentRequirementsQuery(id: string | null) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.states.attachmentRequirementsV2(id),
    queryFn: async () =>
      await statesAdminV2Service.attachmentRequirements(accessToken, id as string),
    enabled: Boolean(accessToken) && Boolean(id),
    refetchOnWindowFocus: false,
    retry: false
  })
}
